import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API, DEFAULT_PRICING, formatLei } from "@/lib/api";

const DEPTHS = [];
for (let d = DEFAULT_PRICING.min_depth; d <= DEFAULT_PRICING.max_depth; d += DEFAULT_PRICING.step) {
  DEPTHS.push(d);
}

const EMPTY = {
  name: "",
  phone: "",
  locality: DEFAULT_PRICING.localities[0].name,
  depth_m: "60",
  message: "",
};

const inputCls = "h-11 rounded-xl bg-white text-slate-900 placeholder:text-slate-400";
const selectCls = "h-11 rounded-xl bg-white text-slate-900";

export default function QuoteFormModal({ open, onOpenChange, prefill }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && prefill) {
      setForm((f) => ({
        ...f,
        locality: prefill.locality || f.locality,
        depth_m: String(prefill.depth || f.depth_m),
      }));
    }
  }, [open, prefill]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const digits = form.phone.replace(/\D/g, "");
    if (form.name.trim().length < 2) return setError("Completează numele.");
    if (!/^0\d{9}$/.test(digits)) return setError("Telefonul trebuie să fie de forma 07xxxxxxxx.");
    setSending(true);
    try {
      await axios.post(`${API}/quote`, {
        name: form.name.trim(),
        phone: digits,
        locality: form.locality,
        depth_m: Number(form.depth_m),
        casing: prefill?.casingId || "d125",
        estimate_min: prefill?.estimate_min || 0,
        estimate_max: prefill?.estimate_max || 0,
        message: form.message || null,
      });
      toast.success("Cerere trimisă!", {
        description: "Te sunăm noi în cel mai scurt timp pentru detalii și programare.",
      });
      setForm(EMPTY);
      onOpenChange(false);
    } catch (err) {
      toast.error("Nu am putut trimite cererea.", {
        description: "Sună-ne direct la 0729 325 082.",
      });
    } finally {
      setSending(false);
    }
  };

  const groups = DEFAULT_PRICING.localities.reduce((acc, l) => {
    (acc[l.group] = acc[l.group] || []).push(l);
    return acc;
  }, {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-modal"
        className="max-h-[90vh] overflow-y-auto rounded-3xl bg-white p-7 text-slate-900 sm:max-w-md md:p-8"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold text-slate-900">
            Solicită oferta
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Lași datele și te sunăm noi pentru detalii și programare.
            {prefill?.estimate_max > 0 && (
              <>
                {" "}
                Estimarea ta:{" "}
                <strong className="text-hydro-blue">
                  {formatLei(prefill.estimate_min)}–{formatLei(prefill.estimate_max)} lei
                </strong>
                , {prefill.depth} m, {prefill.locality}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="quote-name">Nume</Label>
            <Input
              id="quote-name"
              data-testid="quote-name-input"
              value={form.name}
              onChange={(e) => set("name")(e.target.value)}
              placeholder="Numele tău"
              className={inputCls}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quote-phone">Telefon</Label>
            <Input
              id="quote-phone"
              data-testid="quote-phone-input"
              type="tel"
              inputMode="numeric"
              value={form.phone}
              onChange={(e) => set("phone")(e.target.value)}
              placeholder="07xx xxx xxx"
              className={inputCls}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Localitate</Label>
              <Select value={form.locality} onValueChange={set("locality")}>
                <SelectTrigger data-testid="quote-locality-select" className={selectCls}>
                  <SelectValue placeholder="Localitate" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(groups).map(([g, items]) => (
                    <SelectGroup key={g}>
                      <SelectLabel>{g}</SelectLabel>
                      {items.map((l) => (
                        <SelectItem key={l.name} value={l.name}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Adâncime estimată</Label>
              <Select value={form.depth_m} onValueChange={set("depth_m")}>
                <SelectTrigger data-testid="quote-depth-select" className={selectCls}>
                  <SelectValue placeholder="Adâncime" />
                </SelectTrigger>
                <SelectContent>
                  {DEPTHS.map((d) => (
                    <SelectItem key={d} value={String(d)}>
                      {d} m
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quote-message">Detalii (opțional)</Label>
            <Textarea
              id="quote-message"
              data-testid="quote-message-input"
              value={form.message}
              onChange={(e) => set("message")(e.target.value)}
              placeholder="De ex.: teren cu nisip, puț pentru casă și grădină..."
              rows={3}
              className="rounded-xl bg-white text-slate-900 placeholder:text-slate-400"
            />
          </div>
          {error && (
            <p data-testid="quote-form-error" className="text-sm font-semibold text-red-600">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={sending}
            data-testid="quote-submit-button"
            className="h-12 w-full rounded-full bg-hydro-blue text-base font-bold hover:bg-hydro-cyan hover:text-hydro-navy"
          >
            {sending ? "Se trimite..." : "Trimite cererea"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
